import {Box, Typography} from '@mui/material';
import {Add} from '@mui/icons-material';
import {FC, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {Table} from 'src/components/tables/Table';
import {TableLayout} from 'src/components/tables/TableLayout';
import {EmptyTable} from 'src/components/tables/EmptyTable';
import {ResponsiveTable} from 'src/components/common/ResponsiveTable';
import {CardList} from 'src/components/common/CardList';
import {CompanyEmployeesTableProps} from './CompanyEmployeesTable.types';
import {columnConfigCompanyEmployees} from './CompanyEmployeesTable.tableConfig';
import {CompanyEmployeeItemCard} from './CompanyEmployeeItemCard';
import {addButtonSx, tableContainerSx} from './CompanyEmployeesTable.sx';
import {employeeApi} from 'src/api';
import {EmployeeSortFieldEnum} from 'src/enums';
import AMPModal from 'src/components/common/AMPModal';
import {EmployeeForm} from 'src/components/forms/EmployeeForm';

export const CompanyEmployeesTable: FC<CompanyEmployeesTableProps> = ({
                                                                          companyId,
                                                                          businessId,
                                                                      }) => {
    const intl = useIntl();
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    // Fetch employees for this company
    const {data: employeesData} = employeeApi.useSearchEmployeesQuery({
        params: {
            page: 0,
            size: 100,
        },
        data: {
            keyword: '',
            businessId: businessId,
            companyId: companyId,
            sortAsc: true,
            sortField: EmployeeSortFieldEnum.EMAIL,
        },
    }, {
        skip: !companyId || !businessId,
    });

    const handleOpenAddModal = () => {
        setIsAddModalOpen(true);
    };

    const handleCloseAddModal = () => {
        setIsAddModalOpen(false);
    };

    const handleAddEmployee = async () => {
        // Handle employee creation - this will be implemented with the form
        handleCloseAddModal();
    };

    // Transform data for DataGrid - the employee already has id field
    const tableRows = employeesData?.content || [];

    return (
        // Flush inside the relationships card: rows and an add-row footer, no second card.
        <TableLayout
            flush
            hideFooter
            footerPadded={false}
            footer={(
                <Box
                    sx={addButtonSx}
                    onClick={handleOpenAddModal}
                >
                    <Add sx={{mr: 1, fontSize: '1.25rem'}}/>
                    <Typography
                        variant="body2"
                        color="primary"
                    >
                        <FormattedMessage id="addEmployeeToCompany"/>
                    </Typography>
                </Box>
            )}
        >
            <ResponsiveTable
                responsiveElement={
                    // The grid cannot follow the card's width on a phone, so the rows are the cards
                    // themselves. The empty case keeps the grid's own overlay.
                    tableRows.length ? (
                        <CardList>
                            {tableRows.map((employee) => (
                                <CompanyEmployeeItemCard
                                    key={employee.id}
                                    employee={employee}
                                    businessId={businessId}
                                    companyId={companyId}
                                />
                            ))}
                        </CardList>
                    ) : (
                        <EmptyTable/>
                    )
                }
                desktopElement={
                    <Table
                        rows={tableRows}
                        hideFooter
                        columns={columnConfigCompanyEmployees(businessId, companyId)}
                        sx={{
                            border: 'none',
                            borderRadius: 0,
                            '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
                                outline: 'none !important',
                            },
                        }}
                    />
                }
            />

            <AMPModal
                handleClose={handleCloseAddModal}
                open={isAddModalOpen}
                title={intl.formatMessage({id: 'addEmployee'})}
                type="md"
                fullWidth
                withoutPaddings
            >
                <EmployeeForm
                    handleClose={handleCloseAddModal}
                    isEditMode={false}
                    onSubmit={handleAddEmployee}
                    onCancel={handleCloseAddModal}
                    preselectedCompanyId={companyId}
                />
            </AMPModal>
        </TableLayout>
    );
};
